/**
 * Which turns deserve a "switched model" divider.
 *
 * THE BUG THIS EXISTS TO PREVENT
 * ------------------------------
 * Every message rendered a divider reading "Sonnet → Sonnet".
 *
 * The old rule compared the RAW `model_name` string between consecutive
 * assistant messages. That was correct while a model was one id. Since model
 * families it is not: the same model reaches the transcript under two
 * different strings.
 *
 *   - a live-streamed message carries the picker's FAMILY KEY      → 'sonnet'
 *   - a persisted / reloaded message carries the CONCRETE ID the
 *     backend actually ran                       → 'claude-sonnet-4-5-20250929'
 *
 * Two strings, one model. The raw comparison saw a change, and because both
 * sides resolve to the same display name the divider announced a switch from
 * a model to itself.
 *
 * THE RULE
 * --------
 * Compare IDENTITY, not spelling. `findModelEntry` already resolves a family
 * key, a pinned `resolved_model` and any of a family's `version_ids` to the
 * same catalogue entry, so that entry's id is the canonical identity. A
 * divider appears only when the identity genuinely changes.
 *
 * Family-level is the right granularity, not a compromise: users pick a
 * family, an admin pins which release it runs, and a tenant re-pin is not
 * something to interrupt someone's conversation about.
 */

export interface ModelSwitchMessage {
  message_id: string;
  role?: string;
  model_name?: string;
  /** The release that answered, by name (the server's `answered_by`). When
   *  present it IS the divider's label: the model that ran, not the family's
   *  current pin. */
  answered_by?: { name?: string } | null;
  served_by_fallback?: boolean;
  message_metadata?: { served_by_fallback?: boolean } | Record<string, unknown> | null;
}

/**
 * Messages that must never take part in the switch comparison:
 *
 *  - the live streaming placeholder (`temp-…`): it carries the PICKER's
 *    family, not the model that will actually answer. Comparing it drew a
 *    "Sonnet → Gemini" divider for the second the turn was in flight and
 *    erased it when the persisted reply arrived on Sonnet;
 *  - a reply the failover served on another provider: the user did not
 *    switch anything, so the transcript must not say they did.
 */
export function isSwitchNeutral(msg: ModelSwitchMessage): boolean {
  if (msg.message_id.startsWith('temp-')) return true;
  if (msg.served_by_fallback) return true;
  const meta = msg.message_metadata as { served_by_fallback?: boolean } | null | undefined;
  return !!meta?.served_by_fallback;
}

export interface ModelSwitch {
  from: string;
  to: string;
}

export interface DeriveOptions {
  /** Canonical identity for a model ref (the catalogue entry's id). */
  resolveIdentity?: (ref: string) => string | undefined;
  /** Human-facing name for a model ref. */
  resolveName: (ref: string) => string;
}

/** Gemini refs sometimes arrive prefixed; strip it before any comparison. */
export function normalizeModelRef(ref: string): string {
  return ref.replace(/^models\//, '');
}

/**
 * Map of message_id → the switch to draw ABOVE that assistant message.
 */
export function deriveModelSwitches(
  messages: ModelSwitchMessage[],
  { resolveIdentity, resolveName }: DeriveOptions,
): Record<string, ModelSwitch> {
  const switches: Record<string, ModelSwitch> = {};
  let prev: { identity: string; label: string; resolved: boolean } | undefined;

  for (const msg of messages) {
    if (msg.role !== 'assistant' || !msg.model_name) continue;
    if (isSwitchNeutral(msg)) continue;

    const ref = normalizeModelRef(msg.model_name);
    const identity = resolveIdentity?.(ref);
    const current = {
      identity: identity ?? ref,
      // The release that answered when the server named it; the family's
      // name only for a message stored before answers carried one.
      label: msg.answered_by?.name || resolveName(ref),
      resolved: identity !== undefined,
    };

    // Both sides must have been resolved through the catalogue before we are
    // willing to claim a switch. An unresolved ref cannot be compared: while
    // the catalogue is still loading, 'sonnet' and
    // 'claude-sonnet-4-5-20250929' are just two unequal strings, and
    // comparing them is precisely how the false divider came back. Staying
    // silent for a moment beats announcing a switch that never happened.
    if (prev && prev.resolved && current.resolved && prev.identity !== current.identity) {
      switches[msg.message_id] = { from: prev.label, to: current.label };
    }

    prev = current;
  }

  return switches;
}
